import type { BootstrapData, JsonRecord, SseEvent, TurnRecord } from './api';
import { contentFrom, numberFrom, recordFrom, stringFrom, stringList } from './api';

export function createRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const random = Math.random().toString(16).slice(2);
  return `req-${Date.now().toString(36)}-${random}`;
}

export function formatDate(value?: string | number): string {
  if (value === undefined) return '';
  const date = typeof value === 'number' ? new Date(value < 1e12 ? value * 1000 : value) : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatDuration(value?: number): string | undefined {
  if (value === undefined) return undefined;
  if (value < 1000) return `${Math.round(value)} ms`;
  if (value < 60_000) return `${(value / 1000).toFixed(value < 10_000 ? 1 : 0)} 秒`;
  return `${Math.floor(value / 60_000)} 分 ${Math.round((value % 60_000) / 1000)} 秒`;
}

export function formatBytes(value?: number): string {
  if (value === undefined) return '';
  if (value < 1024) return `${value} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 ** 3) return `${(value / 1024 ** 2).toFixed(1)} MB`;
  return `${(value / 1024 ** 3).toFixed(1)} GB`;
}

export function bootstrapWorkspace(bootstrap?: BootstrapData): string {
  if (!bootstrap) return '工作区未知';
  const workspace = recordFrom(bootstrap.workspace);
  return (
    stringFrom(bootstrap.workspace_name ?? workspace.name) ??
    stringFrom(bootstrap.workspace_path ?? workspace.path) ??
    stringFrom(bootstrap.cwd) ??
    stringFrom(bootstrap.workspace) ??
    '工作区未知'
  );
}

export function bootstrapModel(bootstrap?: BootstrapData): string {
  if (!bootstrap) return '模型未知';
  const model = recordFrom(bootstrap.model);
  return stringFrom(bootstrap.model) ?? stringFrom(model.name ?? model.id) ?? '模型未知';
}

export function bootstrapLocal(bootstrap?: BootstrapData): string {
  if (!bootstrap) return '运行位置未知';
  const runtime = recordFrom(bootstrap.runtime);
  const local = bootstrap.local ?? (typeof runtime.local === 'boolean' ? runtime.local : undefined);
  if (local === true) return '本地运行';
  if (local === false) return '远程运行';
  return stringFrom(runtime.status) ?? '运行位置未知';
}

export function bootstrapPermission(bootstrap?: BootstrapData): string {
  if (!bootstrap) return '权限未知';
  const permissions = recordFrom(bootstrap.permissions);
  const mode = stringFrom(bootstrap.permission_mode ?? permissions.mode)?.toLowerCase();
  if (mode === 'ask') return '按需审批';
  if (mode === 'auto') return '自动授权';
  if (mode === 'deny') return '默认拒绝';
  return mode ?? '权限未知';
}

export function applyEventToTurns(turns: TurnRecord[], event: SseEvent): TurnRecord[] {
  if (!event.turn_id) return turns;
  const next = turns.map((turn) => ({ ...turn }));
  let turn = next.find((item) => item.id === event.turn_id);
  if (!turn) {
    const requestId = stringFrom(event.payload.request_id);
    turn = next.find((item) => item.requestId === requestId);
    if (turn) turn.id = event.turn_id;
  }
  if (!turn) {
    turn = {
      id: event.turn_id,
      input: contentFrom(event.payload.input ?? event.payload.prompt) ?? '',
      output: '',
      status: 'running',
      createdAt: event.ts_ms,
      attachments: [],
      raw: {},
    };
    next.push(turn);
  }

  const type = event.type.toLowerCase();
  const text = eventText(event.payload);
  if (type.includes('delta') || type.includes('chunk')) {
    if (text) turn.output += text;
  } else if (
    type.includes('report.completed') ||
    type.includes('assistant.completed') ||
    type === 'final' ||
    type.includes('response.completed')
  ) {
    if (text) turn.output = text;
    turn.status = 'completed';
    turn.completedAt = event.ts_ms;
  } else if (type.includes('turn.completed') || type.includes('turn.done')) {
    if (text) turn.output = text;
    turn.status = 'completed';
    turn.completedAt = event.ts_ms;
  } else if (type.includes('error') || type.includes('failed')) {
    turn.status = 'error';
    turn.error =
      stringFrom(event.payload.error ?? event.payload.message ?? event.payload.detail) ??
      '执行失败。';
  } else if (type.includes('cancel')) {
    turn.status = 'cancelled';
    turn.error = undefined;
  } else if (type.includes('turn') || type.includes('response') || type.includes('assistant')) {
    turn.status = 'running';
  }

  return next;
}

export function eventText(payload: JsonRecord): string | undefined {
  return contentFrom(
    payload.delta ??
      payload.text ??
      payload.markdown ??
      payload.content ??
      payload.answer ??
      payload.report,
  );
}

export function eventWarnings(event: SseEvent): string[] {
  return stringList(event.payload.warnings ?? event.payload.warning);
}

export function eventDuration(event: SseEvent): number | undefined {
  return numberFrom(event.payload.duration_ms ?? event.payload.elapsed_ms);
}
